// tabs/DocumentsTab.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, Group, Divider, SimpleGrid, Card, ActionIcon, Flex } from '@mantine/core';
import {
  IconTrash, IconFileTypePdf, IconFileTypeDoc, IconFileTypeXls,
  IconFileTypeZip, IconPhoto, IconFile, IconEye,
  IconRefresh,
} from '@tabler/icons-react';
import { useACCounsellingServices, LeaderDocumentView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FileUploader } from '@/components/ccms-components/file-management';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';
import { inferMimeType } from '@/components/ccms-components/file-management/utils';
import type { ViewerFile } from '@/components/ccms-components/file-management/types';
import ConfirmActionModal from './ConfirmActionModal';
import Tooltip from '@/components/ui/overlays/Tooltip';

interface DocumentsTabProps {
  leaderId: string;
  active: boolean;
  onLeaderChange?: () => void;
}

interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

const getFileIcon = (mimeType?: string | null) => {
  if (!mimeType) return IconFile;
  if (mimeType.startsWith('image/')) return IconPhoto;
  if (mimeType === 'application/pdf') return IconFileTypePdf;
  if (mimeType.includes('word') || mimeType.includes('document')) return IconFileTypeDoc;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return IconFileTypeXls;
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return IconFileTypeZip;
  return IconFile;
};

export default function DocumentsTab({ leaderId, active, onLeaderChange }: DocumentsTabProps) {
  const ACCounsellingServices = useACCounsellingServices();

  const [leaderDocuments, setLeaderDocuments] = useState<LeaderDocumentView[]>([]);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  function formatUploadedDate(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return 'Unknown';

  const year   = raw.slice(0, 4);
  const month  = raw.slice(4, 6);
  const day    = raw.slice(6, 8);
  const hour   = raw.slice(8, 10);
  const minute = raw.slice(10, 12);

  const date = `${year}-${month}-${day}`;
  if (raw.length >= 12) return `${date} ${hour}:${minute}`;
  return date;
}

  // ── File viewer state ──────────────────────────────────────────────────
  const [viewerOpen, setViewerOpen]   = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const fetchLeaderDocuments = useCallback(async () => {
    if (!leaderId) return;
    setLoading(true);
    try {
      const result = await ACCounsellingServices.leader.getDocuments(leaderId);
      setLeaderDocuments(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leaderId, ACCounsellingServices]);

  useEffect(() => { if (active) fetchLeaderDocuments(); }, [active, fetchLeaderDocuments]);

 
  const viewerFiles: ViewerFile[] = useMemo(
    () =>
      leaderDocuments.map((d) => {
        const urlFileName = d.url?.split('/').pop();
        const fileName = d.name && d.name.includes('.')
          ? d.name
          : urlFileName || d.name || `Document ${d.documentID}`;
        return {
          id: String(d.documentID),
          fileName,
          mimeType: inferMimeType(String(d.type ?? ''), fileName),
          // Real size since SDK 0.0.54 — `LeaderDocumentView.fileSize` replaced a hardcoded 0.
          fileSize: d.fileSize ?? 0,
          url: d.url ?? undefined,
        };
      }),
    [leaderDocuments]
  );

  const openViewerAt = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const handleUploadComplete = async (files: CompletedUpload[]) => {
    try {
      await Promise.all(
        files.map((file) =>
          ACCounsellingServices.leader.addDocument(leaderId, {
            userId: leaderId,
            name: file.fileName,
            url: file.url,
            type: file.mimeType,
          } as any)
        )
      );
      await fetchLeaderDocuments();
      onLeaderChange?.();
      toast.success(
        'Document Added',
        `${files.length} document${files.length !== 1 ? 's' : ''} uploaded successfully.`
      );
    } catch (e) {
      console.error(e);
      toast.error('Error', 'File uploaded but failed to attach it to the leader.');
    }
  };

  const handleRemoveDocument = (documentId: number, documentName?: string) => {
    setConfirmMessage(`Remove this document${documentName ? ` (${documentName})` : ''} from the leader?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await ACCounsellingServices.leader.removeDocument(leaderId, documentId as any);
        await fetchLeaderDocuments();
        onLeaderChange?.();
        toast.warning('Document Removed', 'Document removed from leader.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove document.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Document"
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      {/* ── Universal file preview ── */}
      <UniversalFileViewer
        files={viewerFiles}
        initialIndex={viewerIndex}
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        onIndexChange={setViewerIndex}
      />

      <Flex justify="right" align="center" mb="md">
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={fetchLeaderDocuments} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Flex>

      <FileUploader
        domain="Leader"
        recordId={leaderId}
        multiple
        accept={['*/*']}
        onUploadComplete={handleUploadComplete}
        onUploadError={(err: any) => {
          console.error(err);
          toast.error('Upload Failed', err?.message ?? 'Could not upload file.');
        }}
      />

      <Divider my="md" />

      {leaderDocuments.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No documents assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {leaderDocuments.map((d, index) => {
            const FileIcon = getFileIcon(d.type);
            return (
              <Card
                key={d.documentID}
                withBorder
                radius="md"
                padding="lg"
                style={{ cursor: 'pointer' }}
                onClick={() => openViewerAt(index)}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                    <FileIcon size={28} />
                    <div style={{ overflow: 'hidden' }}>
                      <Text fw={700} truncate>{d.name ?? `Document ${d.documentID}`}</Text>
                      <Text size="xs" c="dimmed">
                        Uploaded: {formatUploadedDate(d.uploadedDateTime)}
                      </Text>
                    </div>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Preview">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          openViewerAt(index);
                        }}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveDocument(d.documentID, d.name ?? undefined);
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </>
  );
}